import { throttle } from 'lodash';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import { contentPropTypes, linkPropTypes } from '../../../propTypes';

import { getInitialCurrencyState } from '../../../util/currency';

import {
  ContentTypes,
  Features,
  Image,
  PapBookNowButton,
  SplitImage,
  SplitVideo,
  Text,
  Video,
} from '../ContentComponents';
import PageAnchors from '../PageAnchors';
import CUSTOM_EVENT from '../../../util/customEvents';

const propTypes = exact({
  bookNowCta: linkPropTypes.isRequired,
  callToBookText: PropTypes.string.isRequired,
  priceLabelText: PropTypes.string.isRequired,
  priorityAccessPassOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      pcmCode: PropTypes.string,
      tourId: PropTypes.string,
      department: PropTypes.string,
      deadline: PropTypes.string,
      maxPartySize: PropTypes.number,
      emailTemplateKey: PropTypes.string,
      isSoldOut: PropTypes.bool,
      strapLineText: PropTypes.string,
      content: PropTypes.arrayOf(contentPropTypes).isRequired,
      pricePerPersonPerCurrency: PropTypes.arrayOf(
        PropTypes.shape({
          price: PropTypes.number,
          formattedPrice: PropTypes.string,
          currency: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  queryParamKey: PropTypes.string.isRequired,
  soldOutText: PropTypes.string.isRequired,
  perPersonText: PropTypes.string,
});

const defaultProps = {
  perPersonText: 'pp',
};

class PapDetails extends Component {
  constructor(props) {
    super(props);

    this.pageAnchorsRef = React.createRef();
    this.optionRefs = {};

    this.handleScroll = this.handleScroll.bind(this);

    const prices = props.priorityAccessPassOptions[0].pricePerPersonPerCurrency;

    const currentCurrency = getInitialCurrencyState(prices ?? []) ?? {};

    this.state = {
      currentOptionId: null,
      currentCurrency,
    };
  }

  componentDidMount() {
    this.handleScroll();
    this.scrollThrottle = throttle(this.handleScroll, 100);
    window.addEventListener('scroll', this.scrollThrottle, { passive: true });
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.scrollThrottle);
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  handleCurrencyChange = (event) => {
    const userSelectedCurrency = event?.detail;

    this.setState({
      currentCurrency: userSelectedCurrency,
    });
  };

  handleScroll() {
    if (
      this.pageAnchorsRef.current &&
      this.pageAnchorsRef.current.ref.current &&
      Object.keys(this.optionRefs).length
    ) {
      const pageAnchorsHeight =
        this.pageAnchorsRef.current.ref.current.getBoundingClientRect().height + 1;
      let currentOption = null;

      Object.keys(this.optionRefs).some((key) => {
        const option = this.optionRefs[key];
        const optionRect = option.getBoundingClientRect();

        if (optionRect.top <= pageAnchorsHeight && optionRect.bottom >= pageAnchorsHeight) {
          currentOption = option;
          return true;
        }

        return false;
      });

      this.setState({ currentOptionId: currentOption?.id });
    }
  }

  getPapPrice = ({ pricePerPersonPerCurrency }) => {
    const currencyOption = pricePerPersonPerCurrency?.find(
      (price) => price.currency === this.state.currentCurrency.code
    );
    return currencyOption ?? {};
  };

  render() {
    const {
      bookNowCta,
      callToBookText,
      priceLabelText,
      priorityAccessPassOptions,
      queryParamKey,
      soldOutText,
    } = this.props;
    const anchors = priorityAccessPassOptions.map((papOption) => ({
      href: papOption.key,
      text: papOption.name,
    }));

    return (
      <div className="PapDetails">
        <PageAnchors
          anchors={anchors}
          ref={this.pageAnchorsRef}
          activeAnchor={this.state.currentOptionId?.replace('anchor-', '')}
        />
        {priorityAccessPassOptions.map((papOption) => {
          const { price } = this.getPapPrice(papOption);
          const { symbol } = this.state.currentCurrency;
          return (
            <div
              key={papOption.key}
              className="PapDetails_section"
              id={`anchor-${papOption.key}`}
              // eslint-disable-next-line no-return-assign
              ref={(ref) => (this.optionRefs[papOption.key] = ref)}
            >
              <h2 className="PapDetails_header">{papOption.name}</h2>
              <div className="PackageCta">
                <div className="PackageCta_body">
                  <div className="PackageCta_column">
                    <div className="PackageCta_overview">
                      <div className="PackageCta_content">
                        <div className="PackageCta_overviewContent">
                          <h3 className="PackageCta_title">{papOption.name}</h3>
                          {papOption.strapLineText && <span>{papOption.strapLineText}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="PackageCta_price">
                      <div className="PackageCta_content">
                        <div className="PackageCta_priceContent">
                          <span className="PackageCta_sup">{priceLabelText}</span>
                          <span className="PackageCta_perPerson">
                            {symbol}
                            {price}
                            {this.props.perPersonText}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="PackageCta_book">
                      <div className="PackageCta_content">
                        <div className="PackageCta_form">
                          {papOption.isSoldOut && (
                            <span className="PackageCta_soldOut">{soldOutText}</span>
                          )}
                          {papOption.isCallToBook && (
                            <span className="PackageCta_soldOut">{callToBookText}</span>
                          )}
                          {!papOption.isSoldOut && !papOption.isCallToBook && (
                            <a
                              href={`${bookNowCta.url}?${queryParamKey}=${papOption.key}`}
                              className="PackageCta_button Button Button-fill"
                              rel="nofollow"
                            >
                              <span className="Button_text">{bookNowCta.title}</span>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {papOption.content?.map((contentItem) => {
                let component = null;
                let isInset = false;

                switch (contentItem.contentType.toLowerCase()) {
                  case ContentTypes.Text:
                    component = <Text blockName="PapDetails" {...contentItem.properties} />;
                    isInset = true;
                    break;
                  case ContentTypes.Features:
                    component = <Features blockName="PapDetails" {...contentItem.properties} />;
                    isInset = true;
                    break;
                  case ContentTypes.Image:
                    component = <Image {...contentItem.properties} />;
                    break;
                  case ContentTypes.Video:
                    component = <Video {...contentItem.properties} />;
                    break;
                  case ContentTypes.SplitImage:
                    component = <SplitImage {...contentItem.properties} />;
                    break;
                  case ContentTypes.SplitVideo:
                    component = <SplitVideo {...contentItem.properties} />;
                    break;
                  case ContentTypes.PapBookNowButton:
                    component = <PapBookNowButton {...contentItem.properties} />;
                    break;
                  default:
                }

                return (
                  <div key={contentItem.id} className="PapDetails_contentContainer">
                    <div className="PapDetails_content">
                      <div className={`PapDetails_wrap${isInset ? '-inset' : ''}`}>{component}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }
}

PapDetails.propTypes = propTypes;
PapDetails.defaultProps = defaultProps;

export default PapDetails;
